import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Mic, Play } from "lucide-react";
import { PODCAST, PHOTOS } from "@/data/content";

const LOOKUP = "https://itunes.apple.com/lookup?id=1746812846&media=podcast&entity=podcastEpisode&limit=6&sort=recent";

const fmtDur = (ms) => {
  const m = Math.round(ms / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`;
};

export default function Podcast() {
  const [episodes, setEpisodes] = useState([]);

  useEffect(() => {
    fetch(LOOKUP)
      .then((r) => r.json())
      .then((d) => setEpisodes((d.results || []).filter((r) => r.kind === "podcast-episode").slice(0, 5)))
      .catch(() => setEpisodes([]));
  }, []);

  return (
    <section id="podcast" data-testid="podcast-section" className="relative overflow-hidden py-28 lg:py-40">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-8 top-8 select-none font-display text-[16rem] font-light italic leading-none text-[#C5A059]/10"
      >
        06
      </span>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-3 font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
            >
              <Mic size={14} strokeWidth={1.5} /> Chapter 06 — The Podcast
            </motion.p>
            <div className="mt-6 flex items-center gap-6">
              <motion.img
                src={PHOTOS.podcastCover}
                alt="Infinite Life, Infinite Wisdom — podcast cover"
                data-testid="podcast-cover"
                className="hidden w-28 border border-[#C5A059]/50 object-cover sm:block lg:w-36"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              />
              <motion.h2
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
                className="font-display2 uppercase leading-[0.88] tracking-[0.01em] text-[#6B5D4F] text-5xl sm:text-6xl lg:text-7xl"
              >
                Infinite life, <br />
                <span className="font-display normal-case italic tracking-normal text-[#C5A059]">infinite wisdom.</span>
              </motion.h2>
            </div>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-md font-body text-base font-light leading-relaxed text-[#8B7D6B]"
          >
            Conversations on grief, healing, intuition, and the infinite lessons of the human spirit —
            streaming now on Apple Podcasts and Spotify, with 19,000+ monthly listeners worldwide.
          </motion.p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.div
            data-testid="podcast-episodes-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="flex flex-col border border-[#6B5D4F]/10 bg-[#F7F1E6]/60 p-6 lg:col-span-7"
          >
            <div className="mb-5 flex items-center justify-between">
              <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.4em] text-[#8B7D6B]">Latest episodes</p>
              <a
                data-testid="podcast-spotify-link"
                href={PODCAST.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="gold-underline flex items-center gap-2 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F]"
              >
                Spotify <ArrowUpRight size={13} />
              </a>
            </div>
            <div className="flex-1 divide-y divide-[#6B5D4F]/10">
              {episodes.length === 0 && (
                <p className="py-10 text-center font-body text-sm font-light text-[#8B7D6B]">
                  Stream every episode on Apple Podcasts or Spotify.
                </p>
              )}
              {episodes.map((ep, i) => (
                <a
                  key={ep.trackId || i}
                  data-testid={`podcast-episode-${i}`}
                  href={ep.trackViewUrl || PODCAST.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-5 py-4 first:pt-0"
                >
                  <span className="font-display text-lg italic text-[#C5A059]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1">
                    <span className="block font-display text-lg font-light leading-snug text-[#6B5D4F] transition-colors duration-300 group-hover:text-[#C5A059] md:text-xl">
                      {ep.trackName}
                    </span>
                    <span className="mt-1 block font-body text-[0.6rem] font-medium uppercase tracking-[0.25em] text-[#8B7D6B]">
                      {ep.releaseDate ? new Date(ep.releaseDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                      {ep.trackTimeMillis ? ` · ${fmtDur(ep.trackTimeMillis)}` : ""}
                    </span>
                  </span>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#6B5D4F]/20 text-[#6B5D4F] transition-colors duration-300 group-hover:border-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-[#FDFAF4]">
                    <Play size={12} className="ml-0.5" />
                  </span>
                </a>
              ))}
            </div>
            <div className="mt-auto flex flex-wrap gap-3 pt-6">
              <a
                data-testid="podcast-listen-apple-btn"
                href={PODCAST.apple}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#6B5D4F] px-6 py-3 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#FDFAF4] transition-colors duration-500 hover:bg-[#C5A059]"
              >
                Apple Podcasts
              </a>
              <a
                data-testid="podcast-listen-spotify-btn"
                href={PODCAST.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-[#6B5D4F] px-6 py-3 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F] transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
              >
                Spotify
              </a>
            </div>
          </motion.div>

          <motion.div
            data-testid="podcast-apple-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.12, ease: [0.76, 0, 0.24, 1] }}
            className="border border-[#6B5D4F]/10 bg-[#F7F1E6]/60 p-6 lg:col-span-5"
          >
            <div className="mb-6 flex items-center justify-between">
              <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.4em] text-[#8B7D6B]">The Show</p>
              <a
                data-testid="podcast-apple-link"
                href={PODCAST.apple}
                target="_blank"
                rel="noopener noreferrer"
                className="gold-underline flex items-center gap-2 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F]"
              >
                Apple Podcasts <ArrowUpRight size={13} />
              </a>
            </div>
            <div className="flex flex-col items-center py-2 text-center">
              <motion.img
                src={PHOTOS.podcastCover}
                alt="Infinite Life, Infinite Wisdom — podcast cover"
                data-testid="podcast-show-cover"
                className="w-44 border border-[#C5A059]/50 object-cover shadow-[0_20px_40px_-15px_rgba(30,27,24,0.3)]"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.5 }}
              />
              <p className="mt-6 max-w-xs font-serif2 text-xl italic leading-snug text-[#6B5D4F]/85">
                "Conversations that turn loss into light."
              </p>
              <p className="mt-3 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">
                Hosted by Susan Grau
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <a
                  data-testid="podcast-card-apple-btn"
                  href={PODCAST.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#6B5D4F] px-6 py-3 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#FDFAF4] transition-colors duration-500 hover:bg-[#C5A059]"
                >
                  Apple Podcasts
                </a>
                <a
                  data-testid="podcast-card-spotify-btn"
                  href={PODCAST.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#6B5D4F] px-6 py-3 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F] transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
                >
                  Spotify
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
